package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/appstatetransform")
@Composable
fun AppStateTransformPage() {
    BlogLayout(
        PageProperties(
            "AppState Transform",
            ""
        ),
        BlogProperties("Jetpack Compose", "AppState Transform (1.0.0-alpha01) - Compose’s Reactive State Beyond the UI")
    )
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {

            KotlinCode(
                code = """
                    [versions]
                    appstate = "1.0.0-alpha01"

                    [libraries]
                    androidx-appstate-transform = {
                        module = "androidx.appstate.transform:transform",
                        version.ref = "appstate"
                    }
                    
                    dependencies {
                        implementation(
                            libs.androidx.appstate.transform
                        )
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "How Compose’s headless Composition can simplify reactive state management, combine different data sources, and reduce the complexity of application state.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose, you’re probably familiar with mutableStateOf, StateFlow, collectAsState(), derivedStateOf and Kotlin Flow operators such as combine().",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Consider a task management application. Its dashboard needs to display tasks based on:",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Tasks retrieved from a database.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Network connectivity.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Selected category",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "User preferences, such as whether completed tasks should be displayed.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Different data sources depending on whether the device is online or offline.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "AppState Transform",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "AppState Transform is built on the Compose runtime. It allows developers to execute a @Composable block without actually rendering any UI.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = " Compose State is a value holder backed by the snapshot system, and the snapshot system has no dependency on drawing pixels. The guide lists two benefits of using it beyond the UI. Dependency tracking is automatic and dynamic, so Compose doesn't observe a branch you didn't take. And you can update several MutableState properties atomically with Snapshot.withMutableSnapshot, instead of choosing between one giant StateFlow<UiState> and several flows that can't change together.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State → Composable Transformation → Derived State",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Transform: Combines and transforms different reactive state sources into a single Compose State. Transform runs a @Composable block without any UI and turns whatever it reads into a Compose State.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "StateStore: Provides a type-safe mechanism for storing and sharing application state across different components.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    data class Task(
                        val id: Int,
                        val title: String,
                        val category: String,
                        val isCompleted: Boolean
                    )
                    
                    sealed interface TaskUiState {
                        
                        data object Loading : TaskUiState
                        
                        data class Success(
                            val tasks: List<Task>,
                            val isOnline: Boolean
                        ) : TaskUiState
                    }
                    
                    interface TaskRepository {
                        fun observeRemoteTasks(): Flow<List<Task>>
                        fun observeLocalTasks(): Flow<List<Task>>
                    }
                    
                    interface NetworkMonitor {
                         val isOnline: State<Boolean>
                    }
                    
                    class TaskPreferences {

                        var selectedCategory by mutableStateOf<String?>(null)
                            private set
                        
                        var showCompleted by mutableStateOf(false)
                            private set
                        
                        fun updateCategory(category: String?) {
                            selectedCategory = category
                        }
                        
                        fun updateShowCompleted(show: Boolean) {
                            showCompleted = show
                        }
                    }
                    
                    class TaskViewModel(
                        private val repository: TaskRepository,
                        private val networkMonitor: NetworkMonitor,
                        private val preferences: TaskPreferences
                    ) : ViewModel() {

                       val uiState: State<TaskUiState> =
                            transform<TaskUiState>(
                                initialValue = TaskUiState.Loading,
                                scope = viewModelScope
                            ) { previous ->
                                
                                // Read Compose State directly
                                val isOnline = networkMonitor.isOnline.value
                                val category = preferences.selectedCategory
                                val showCompleted = preferences.showCompleted
                                
                                // Select the appropriate data source
                                val tasks = if (isOnline) {
                                    repository.observeRemoteTasks()
                                        .collectAsState(initial = null).value
                                } else {
                                    repository.observeLocalTasks()
                                        .collectAsState(initial = null).value
                                } ?: return@transform previous
                                
                                // Apply filters
                                val filteredTasks = tasks.filter { task ->
                                    (showCompleted || !task.isCompleted) &&
                                        (category == null || task.category == category)
                                }
                               
                                TaskUiState.Success(
                                    tasks = filteredTasks,
                                    isOnline = isOnline
                                )
                            }
                    }
                    
                    @Composable
                    fun TaskScreen(
                        viewModel: TaskViewModel
                    ) {
                        val uiState by viewModel.uiState

                        when (val state = uiState) {
                            TaskUiState.Loading -> {
                                CircularProgressIndicator()
                            }
                            
                            is TaskUiState.Success -> {
                                Column {
                                    Text(
                                        text = if (state.isOnline) {
                                            "Online"
                                        } else {
                                            "Offline"
                                        }
                                    )
                                    LazyColumn {
                                        items(state.tasks) { task ->
                                            Text(task.title)
                                        }
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Search using Transform",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @HiltViewModel
                    class FlightSearchViewModel @Inject constructor(
                        private val flightsRepository: FlightsRepository,
                        private val networkMonitor: NetworkMonitor,
                        savedStateHandle: SavedStateHandle,
                    ) : ViewModel() {
                    
                        var selectedFlightIds by mutableStateOf(emptySet<String>())
                            private set
                        var query by savedStateHandle.saveable { mutableStateOf("") }
                            private set
                            
                        fun updateQuery(newQuery: String) {
                            Snapshot.withMutableSnapshot {
                                query = newQuery
                                selectedFlightIds = emptySet()
                            }
                        }
                        
                        val uiState: State<SearchUiState> = transform<SearchUiState>(
                            initialValue = SearchUiState.Loading,
                            scope = viewModelScope,
                        ) {
                            val flights = remember(query) {
                                flightsRepository.search(query)
                            }.collectAsState(initial = null).value
                                ?: return@transform SearchUiState.Loading
                            SearchUiState.Success(
                                flights = flights,
                                selectedIds = selectedFlightIds,
                                isOnline = networkMonitor.isOnline.value,
                            )
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://medium.com/@soumikcse07/jetpack-appstate-transform-taking-composes-reactive-state-beyond-the-ui-d67bc7c95a48
//https://chetan-garg36.medium.com/is-viewmodel-dead-appstate-is-coming-8cd057d0abaf?sk=60a8d52c87ce2fe7f63ea2afe15435d1